/**
 * Fetch function for Jira issue → PR hydration.
 *
 * Mirrors the pattern of fetchJiraFieldsQuery in ./index.tsx:
 * - Uses fetchAgg to call AGG
 * - Wraps with inMemoryCache for deduplication
 * - Parses response into IssueLinkedPRs mapping
 */

import { type AggRequest, type AggResponse, fetchAgg } from '../../../../../common/utils/fetch-agg';
import { generateHashFromVariables } from '../../../../../utils/caching/common/key-generator';
import { inMemoryCache } from '../../../../../utils/caching/in-memory';

import {
	buildPRQueryVariables,
	jiraPRQueryGenerator,
	MAX_ISSUES_PER_QUERY,
	PR_LIMIT_PER_ISSUE,
	type SearchPageJiraPRHydrationQueryVariables,
} from './pr-query-generator';
import {
	type DevOpsReviewer,
	type HydratedPullRequest,
	type ParsedIssueLinkedPRs,
	type JiraPRHydrationData,
} from './pr-types';

const cache = inMemoryCache<AggResponse<JiraPRHydrationData>>();

const generateCacheKeyForJiraPRs = (issueAris: string[]): string => {
	// Sort for stable cache keys regardless of order
	const keyObj = { issueAris: [...issueAris].sort() };
	return generateHashFromVariables(keyObj);
};

// Expire cache on errors or empty response
const expireWhen = (result: AggResponse<JiraPRHydrationData>): boolean => {
	const { data, errors } = result;
	if (!data || (errors && errors.length > 0)) {
		return true;
	}
	// Check if graphStore has any data at all
	return Object.keys(data.graphStore ?? {}).length === 0;
};

export type SearchPageJiraPRHydrationQueryProps = Pick<
	AggRequest<SearchPageJiraPRHydrationQueryVariables>,
	'aggAbsoluteUrl'
> & {
	issueAris: string[];
	cloudId: string;
};

/**
 * Splits an array into chunks of the specified size.
 */
const chunk = <T,>(arr: T[], size: number): T[][] => {
	const chunks: T[][] = [];
	for (let i = 0; i < arr.length; i += size) {
		chunks.push(arr.slice(i, i + size));
	}
	return chunks;
};

/**
 * Fetches linked PR data for a single chunk of Jira issue ARIs via AGG.
 * This is the underlying fetch that handles a batch within MAX_ISSUES_PER_QUERY.
 */
const fetchJiraPRsChunk = ({
	issueAris,
	aggAbsoluteUrl,
	cloudId,
}: SearchPageJiraPRHydrationQueryProps): Promise<AggResponse<JiraPRHydrationData>> => {
	const graphQuery = jiraPRQueryGenerator(issueAris);
	const variables = buildPRQueryVariables(issueAris);

	return cache.inMemoryDecorator(
		generateCacheKeyForJiraPRs(issueAris),
		() =>
			fetchAgg<SearchPageJiraPRHydrationQueryVariables, JiraPRHydrationData>({
				variables,
				aggAbsoluteUrl,
				graphQuery,
				operationName: 'SearchPageJiraPRHydration',
				headers: {
					'X-Query-Context': `ari:cloud:platform::site/${cloudId}`,
				},
			}),
		expireWhen,
	);
};

/**
 * Merges multiple chunked AGG responses into a single response.
 * Combines all graphStore aliases and aggregates errors.
 */
const mergeChunkedResponses = (
	responses: AggResponse<JiraPRHydrationData>[],
): AggResponse<JiraPRHydrationData> => {
	const mergedGraphStore: Record<string, unknown> = {};
	const allErrors: unknown[] = [];

	responses.forEach((response, chunkIndex) => {
		if (response.data?.graphStore) {
			// Re-key aliases from each chunk to use global indices
			// Chunk responses use local aliases (issue0, issue1, ...) which we
			// remap to the global position so parseJiraPRResponse works correctly.
			const chunkOffset = chunkIndex * MAX_ISSUES_PER_QUERY;
			for (const [localKey, value] of Object.entries(response.data.graphStore)) {
				const localIndex = parseInt(localKey.replace('issue', ''), 10);
				const globalKey = `issue${chunkOffset + localIndex}`;
				mergedGraphStore[globalKey] = value;
			}
		}
		if (response.errors) {
			allErrors.push(...response.errors);
		}
	});

	return {
		data: { graphStore: mergedGraphStore },
		...(allErrors.length > 0 ? { errors: allErrors } : {}),
	} as AggResponse<JiraPRHydrationData>;
};

/**
 * Fetches linked PR data for a batch of Jira issue ARIs via AGG.
 *
 * Uses GraphQL aliases to batch issues into AGG requests. If the number of
 * issues exceeds MAX_ISSUES_PER_QUERY (100), the request is automatically
 * chunked into parallel requests and the results are merged.
 *
 * Results are cached in-memory per chunk to avoid redundant fetches.
 */
export const fetchJiraPRsQuery = async ({
	issueAris,
	aggAbsoluteUrl,
	cloudId,
}: SearchPageJiraPRHydrationQueryProps): Promise<AggResponse<JiraPRHydrationData>> => {
	if (issueAris.length <= MAX_ISSUES_PER_QUERY) {
		return fetchJiraPRsChunk({ issueAris, aggAbsoluteUrl, cloudId });
	}

	const chunks = chunk(issueAris, MAX_ISSUES_PER_QUERY);
	const responses = await Promise.all(
		chunks.map((chunkAris) => fetchJiraPRsChunk({ issueAris: chunkAris, aggAbsoluteUrl, cloudId })),
	);

	return mergeChunkedResponses(responses);
};

/**
 * Parses the raw AGG response into a clean IssueLinkedPRs mapping.
 *
 * Maps each issue ARI to its array of hydrated pull requests.
 *
 * @param response - Raw AGG response from fetchJiraPRsQuery
 * @param issueAris - The original issue ARIs (in order) used to map aliases back
 * @returns Mapping from issue ARI → HydratedPullRequest[]
 */
export const parseJiraPRResponse = (
	response: AggResponse<JiraPRHydrationData>,
	issueAris: string[],
): ParsedIssueLinkedPRs => {
	const result: ParsedIssueLinkedPRs = {};
	const graphStore = response.data?.graphStore;

	if (!graphStore) {
		return result;
	}

	issueAris.forEach((ari, index) => {
		const aliasKey = `issue${index}`;
		const connection = graphStore[aliasKey];
		const prs: HydratedPullRequest[] = [];
		let totalEdgeCount = 0;

		if (connection?.edges) {
			totalEdgeCount = connection.edges.length;
			for (const edge of connection.edges) {
				if (edge?.node) {
					prs.push({
						id: edge.node.id,
						title: edge.node.title,
						url: edge.node.url,
						pullRequestInternalId: edge.node.pullRequestInternalId,
						status: edge.node.status,
						lastUpdated: edge.node.lastUpdated,
						reviewers: (edge.node.reviewers ?? []).filter(
							(r): r is DevOpsReviewer => r?.approvalStatus != null,
						),
					});
				}
			}
		}

		// If the API returned its maximum (PR_LIMIT_PER_ISSUE), there may be
		// more PRs we haven't seen, so we can't show an exact count.
		const hitApiLimit = totalEdgeCount >= PR_LIMIT_PER_ISSUE;

		result[ari] = {
			status: 'loaded',
			prs,
			hasMore: hitApiLimit,
		};
	});

	return result;
};
